import React, { useEffect, useRef, useState } from 'react';
import Quagga from 'quagga';
import ScannedProductPopup from '../../components/ScannedProductPopup';
import { useLocation, useNavigate } from 'react-router-dom';
import { useScan } from '../../context/InventoryContext';

const InventoryScanner = () => {
  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const [scannedCode, setScannedCode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get('mode');
  const { clearItems } = useScan();

  const title = mode === 'grn' ? '📥 GRN Processing' : '📦 Inventory Count';

  

  // â›”ï¸ Redirect if mode is missing
  useEffect(() => {
    if (!mode) {
      clearItems(); // Clear context
      navigate('/'); // Redirect to home
    }
  }, [mode, navigate, clearItems]);


  useEffect(() => {
    // Push a new entry to the history stack
    window.history.pushState(null, "", window.location.href);

    const handlePopState = (e) => {
      // Prevent back button navigation
      window.history.pushState(null, "", window.location.href);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Stop scanner on unmount
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const stopScanner = () => {
    try {
      Quagga.stop();
    } catch (err) {
      console.warn('Scanner stop error:', err.message);
    }
    
    if (videoRef.current && videoRef.current.srcObject) {
      try {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => track.stop());
      } catch (err) {
        console.warn('Error stopping media tracks:', err.message);
      }
      videoRef.current.srcObject = null;
    }

    setIsScanning(false);

  };

  const startScanner = async () => {
    setIsScanning(true);
    setScannedCode('');
    setError(null);

    try {
      await Quagga.init({
        numOfWorkers: navigator.hardwareConcurrency || 4,
        locate: false, // We are manually specifying the detection area
        inputStream: {
          name: "Live",
          type: "LiveStream",
          target: videoRef.current,
          constraints: {
            facingMode: "environment",
            width: { min: 640 },
            height: { min: 480 },
          },
          locate: false,
          area: {
            top: "30%",     // Start at 40% from the top
            right: "0%",
            left: "0%",
            bottom: "40%"   // Ends at 60%, making it a 20% tall band
          },
          singleChannel: false,
          focusMode: "continuous",
          advanced: [
            { focusMode: "continuous" },
            { zoom: 1.0 },
            { whiteBalance: "continuous" },
            { exposureMode: "continuous" }
          ]
        },

        frequency: 60,
        decoder: {
          readers: ["code_128_reader"]
        },

        debug: false

      }, (err) => {
        if (err) {
          console.error('Quagga initialization error:', err);
          setError('Failed to initialize scanner. Please ensure camera access is allowed.');
          setIsScanning(false);
          return;
        }

        // The video stream is now automatically attached to our video element
        // via the target property in inputStream configuration
        Quagga.start();
      });

      Quagga.onDetected((result) => {

        if (result.codeResult && result.codeResult.code && result.codeResult.code.length >= 12) {
          const code = result.codeResult.code.trim();
          console.log('Scanned:', code);
          setScannedCode(code);
          stopScanner();
          setShowPopup(true);
        }
      });

    } catch (error) {
      console.error('Scanner error:', error);
      setError('Failed to access camera or scan');
      setIsScanning(false);
    }
  };

  const handleScanClick = () => {
    if (isScanning) {
      stopScanner();
    } else {
      startScanner();
    }
  };

  const handleClose = () => {
    if (
      window.confirm(
        'Are you sure you want to exit the scanner? Unsaved progress may be lost.'
      )
    ) {
      stopScanner();
      clearItems();
      navigate('/');
    }
  };

  const handleNext = () => {
    navigate(`/scanned-result?mode=${mode}`, { state: scannedCode ? { code: scannedCode } : {} });
  };

  return (

    <div style={{ padding: 20, position: 'relative' }}>
      {/* Next button */}
      <button
        onClick={handleNext}
        style={{
          position: 'absolute',
          top: 20,
          right: 20,
          padding: '8px 16px',
          backgroundColor: '#28a745',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          fontSize: '1rem',
          cursor: 'pointer'
        }}
      >
        Next
      </button>

      <h2>{title}</h2>

      {error && (
        <div style={{ color: 'red', margin: '10px 0' }}>
          {error} <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Video box for camera */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 500,
          height: 400,
          margin: '20px auto',
          border: '2px solid #ccc',
          borderRadius: 12,
          overflow: 'hidden',
          backgroundColor: '#000',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
        }}
      >
        <div
          ref={videoRef}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        ></div>

        {/* Dimmed overlays */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '30%',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          zIndex: 2
        }} />
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: '40%',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          zIndex: 2
        }} />

        {/* Scanning box frame (optional) */}
        <div style={{
          position: 'absolute',
          top: '30%',
          left: 0,
          width: '100%',
          height: '30%',
          border: '2px solid rgba(236, 245, 255, 0.8)',
          borderRadius: 6,
          zIndex: 3,
          pointerEvents: 'none',
        }} />

        {/* Blinking blue line */}
        <div
          style={{
            position: 'absolute',
            top: '45%',
            left: 0,
            width: '100%',
            height: 2,
            backgroundColor: 'rgba(0, 123, 255, 0.8)',
            animation: 'blinkLine 1.2s infinite',
            zIndex: 4,
          }}
        />

        {/* Canvas overlay */}
        <canvas
          ref={overlayRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 1
          }}
        />
      </div>

      <style>
        {`
          @keyframes blinkLine {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 1; }
          }
        `}
      </style>


      {/* Bottom Buttons */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          maxWidth: 400,
          margin: '0 auto',
          marginTop: 20
        }}
      >
        <button
          onClick={handleClose}
          style={{
            flex: 1,
            marginRight: 10,
            padding: '10px',
            backgroundColor: '#6c757d',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: '1rem',
            cursor: 'pointer'
          }}
        >
          Close
        </button>
        <button
          onClick={handleScanClick}
          style={{
            flex: 1,
            marginLeft: 10,
            padding: '10px',
            backgroundColor: isScanning ? '#dc3545' : '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: '1rem',
            cursor: 'pointer'
          }}
        >
          {isScanning ? 'Stop Scan' : 'Start Scan'}
        </button>
      </div>

      {/* Scanned Product Popup */}
      {showPopup && (
        <ScannedProductPopup
          code={scannedCode}
          onSubmit={item => {
            console.log('Add to cart:', item);
            setShowPopup(false);
          }}
          onClose={() => setShowPopup(false)}
        />
      )}
    </div>
  );
};

export default InventoryScanner;
